import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, Mail, LogOut, X, AlertCircle } from 'lucide-react';

export const PendingApproval: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pendingState = (location.state as { message?: string; email?: string } | null) ?? null;
  // 소셜 신규 가입 시 location.state로 전달된 메시지/이메일 (미로그인 상태)
  const stateMessage = pendingState?.message;
  const pendingEmail = user?.email || pendingState?.email;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Clock className="w-16 h-16 text-yellow-500" />
          </div>
          <CardTitle className="text-2xl">승인 대기 중</CardTitle>
          <CardDescription>
            {stateMessage ? (
              <span style={{ whiteSpace: 'pre-line' }}>{stateMessage}</span>
            ) : (
              <>
                회원가입이 완료되었습니다.
                <br />
                관리자 승인 후 서비스를 이용하실 수 있습니다.
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-gray-700">
                <p className="font-semibold mb-1">이메일 확인</p>
                <p>
                  {pendingEmail ? (
                    <>가입하신 이메일(<strong>{pendingEmail}</strong>)로 안내 메일이 발송되었습니다.</>
                  ) : (
                    <>가입하신 이메일로 승인 안내 메일이 발송됩니다.</>
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="text-center text-sm text-gray-600">
            <p>승인 완료 시 이메일로 안내드립니다.</p>
            <p className="mt-2">문의사항이 있으시면 관리자에게 연락해주세요.</p>
            <p className="font-semibold mt-1">alt.generation@outlook.com</p>
          </div>

          {user ? (
            <Button onClick={handleLogout} variant="outline" className="w-full">
              <LogOut className="w-4 h-4 mr-2" />
              로그아웃
            </Button>
          ) : (
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              메인 페이지로
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const ApprovalRejected: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <X className="w-10 h-10 text-red-600" />
            </div>
          </div>
          <CardTitle className="text-2xl text-red-600">승인 거부</CardTitle>
          <CardDescription>회원가입이 거부되었습니다.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm font-semibold mb-2">거부 사유</p>
            <p className="text-sm text-gray-700">
              {user?.approvalReason || '관리자가 회원가입을 거부했습니다.'}
            </p>
          </div>

          <div className="text-center text-sm text-gray-600">
            <p>문의사항이 있으시면 관리자에게 연락해주세요.</p>
            <p className="font-semibold mt-1">alt.generation@outlook.com</p>
          </div>

          <Button onClick={handleLogout} variant="outline" className="w-full">
            <LogOut className="w-4 h-4 mr-2" />
            로그아웃
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export const Unauthorized: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-yellow-600" />
            </div>
          </div>
          <CardTitle className="text-2xl">접근 권한 없음</CardTitle>
          <CardDescription>이 페이지에 접근할 권한이 없습니다.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center text-sm text-gray-600">
            <p>관리자 권한이 필요한 페이지입니다.</p>
            <p className="mt-2">권한이 필요하시면 관리자에게 문의해주세요.</p>
          </div>

          <Button onClick={() => navigate('/dashboard')} className="w-full">
            대시보드로 돌아가기
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
